import { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Modal,
  Spin,
  message,
  Pagination,
  Result,
  Tooltip,
  Form,
  Input,
  Popconfirm,
  Space,
} from "antd";
import { ThemeContext } from "../../context/ThemeContext";
import {
  ArrowLeftOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  BarChartOutlined,
  SafetyOutlined,
  FileTextOutlined,
  FormOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/apiClient";

import { usePolicy } from "../../context/PolicyContext";
import { hasPolicyPermission } from "../../utils/policyUtils";
import { isAdmin } from "../../utils/roleUtils";
import "./IssuerDetails.css";
import "../Admin/AdminPages.css";

const { Title, Text, Paragraph } = Typography;

function IssuerISINs() {
  const { policies } = usePolicy();
  console.log("policies", policies);
  const canEdit = hasPolicyPermission(policies, "api:isin:edit");
  const canDelete = hasPolicyPermission(policies, "api:isin:delete");
  console.log(canDelete, canEdit)
  const location = useLocation();
  const cin = location.state?.cin;
  const issuerName = location.state?.issuerName; 
  console.log("issuerName", issuerName);
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);

  const [isinsData, setIsinsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [selectedISIN, setSelectedISIN] = useState(null);
  const [noContent, setNoContent] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editForm] = Form.useForm();

  const baseUrl =
    process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";

  // Helper function to format array values
  const formatArrayValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    return value || "N/A";
  };

  // Fetch ISINs data
  const fetchIsinsData = async (page = 1, pageSize = 10) => {
    setLoading(true);
    setError(null);
    setNoContent(false);
    try {
      const headers = {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "application/json",
      };
      let url;
      if (cin) {
        url = `${baseUrl}/master-isin/cin/${encodeURIComponent(cin)}?page=${page - 1}&size=${pageSize}`;
      } else {
        url = `${baseUrl}/master-isin/all?page=${page - 1}&size=${pageSize}`;
      }
      const response = await apiClient.get(url, { headers, validateStatus: () => true });
      if (response.status === 204) {
        setNoContent(true);
        setIsinsData([]);
        setPagination({ current: 1, pageSize: 10, total: 0, totalPages: 0 });
        return;
      }
      if (response.data.statusCode === 200) {
        const data = response.data.data;
        setIsinsData(data.content || []);
        setPagination({
          current: (data.pageNumber || 0) + 1,
          pageSize: data.pageSize || 10,
          total: data.totalElements || 0,
          totalPages: data.totalPages || 0,
        });
      } else {
        throw new Error(response.data.message || "Failed to fetch ISINs data");
      }
    } catch (error) {
      setError("Error fetching ISINs data");
      message.error("Error fetching ISINs data");
      console.error("Error fetching ISINs data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIsinsData();
    // eslint-disable-next-line
  }, [cin]);

  // Stats for hero section
  const stats = [
    {
      number: pagination.total.toLocaleString(),
      label: "Total ISINs",
      icon: <DatabaseOutlined />,
    },
    {
      number: isinsData.filter(item => item.listingStatus === "Listed").length.toLocaleString(),
      label: "Listed ISINs",
      icon: <BarChartOutlined />,
    },
  ];

  const handleRefresh = () => {
    fetchIsinsData(pagination.current, pagination.pageSize);
  };

  // Back button handler
  const handleGoBack = () => {
    navigate(-1);
    // if (cin) {
    //   navigate("/d/issuer/details", { state: { cin } });
    // } else {
    //   navigate("/d/issuer-list");
    // }
  };

  const handleShowDetails = (record) => {
    console.log("Selected ISIN record:", record);

    // Conditionally navigate based on user role
    if (isAdmin()) {
      navigate("/admin/isin/details", { state: { isin: record.isin } });
    } else {
      navigate("/d/isin/details", { state: { isin: record.isin } });
    }
  };

  // Open edit modal and fetch data
  const handleEdit = async (isin) => {
    setEditLoading(true);
    setEditModalVisible(true);
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      const response = await apiClient.get(
        `${baseUrl}/master-isin/variable-data/${encodeURIComponent(isin)}`,
        { headers, validateStatus: () => true }
      );
      if (response.data && response.data.statusCode === 200 && response.data.data) {
        setEditData(response.data.data);
        editForm.setFieldsValue(response.data.data);
      } else {
        setEditData(null);
        message.error("Failed to fetch ISIN data for editing.");
        setEditModalVisible(false);
      }
    } catch (err) {
      setEditData(null);
      message.error("Error fetching ISIN data for editing.");
      setEditModalVisible(false);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle update submit
  const handleUpdate = async () => {
    try {
      let values = await editForm.validateFields();
      delete values.createdAt;
      delete values.updatedAt;
      setEditLoading(true);
      const headers = {
        "Content-Type": "application/json",
      };
      const response = await apiClient.put(
        `${baseUrl}/master-isin/${encodeURIComponent(editData.isin)}`,
        values,
        { headers, validateStatus: () => true }
      );
      if (response.data && response.data.statusCode === 200) {
        message.success("ISIN updated successfully.");
        setEditModalVisible(false);
        // Optionally, refresh the details modal data
        fetchIsinsData(pagination.current, pagination.pageSize);
      } else {
        message.error(response.data?.message || "Failed to update ISIN.");
      }
    } catch (err) {
      message.error("Error updating ISIN.");
    } finally {
      setEditLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async (isin) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      await apiClient.delete(`${baseUrl}/master-isin/${encodeURIComponent(isin)}`, { headers });
      message.success("ISIN deleted successfully.");
      fetchIsinsData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Failed to delete ISIN.");
    }
  };

  // Important columns for table view
  const tableColumns = [
    {
      title: "ISIN",
      dataIndex: "isin",
      key: "isin",
      render: (text) => <Text strong>{text || "N/A"}</Text>,
    },
    {
      title: "Company Name",
      dataIndex: "companyIssuerName",
      key: "companyIssuerName",
      render: (text) => <Text>{text || "N/A"}</Text>,
    },
    {
      title: "Listing Status",
      dataIndex: "listingStatus",
      key: "listingStatus",
      render: (text) => (
        <Tag color={text === "Listed" ? "green" : "red"}>{text || "N/A"}</Tag>
      ),
    },
    {
      title: "Stock Exchange",
      dataIndex: "stockExchange",
      key: "stockExchange",
      render: (text) => (
        <Tag color="blue">
          {formatArrayValue(text)}
        </Tag>
      ),
    },
    {
      title: "Allotment Date",
      dataIndex: "allotmentDate",
      key: "allotmentDate",
      render: (text) => <Text>{text || "N/A"}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => handleShowDetails(record)}
              type="default"
              size="small"
              style={{ 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'white',
                borderColor: '#1890ff',
                color: '#1890ff',
                borderRadius: 8
              }}
            />
          </Tooltip>
          <>
            {canEdit &&
              (
                <Tooltip title="Edit">
                  <Button 
                    icon={<FormOutlined />} 
                    onClick={() => handleEdit(record.isin)}
                    type="default"
                    size="small"
                    style={{ 
                      width: 40, 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'white',
                      borderColor: '#52c41a',
                      color: '#52c41a',
                      borderRadius: 8
                    }}
                  />
                </Tooltip>
              )
            }
          </>
          {
            canDelete && (
              <Tooltip title="Delete">
                <Popconfirm
                  title="Are you sure you want to delete this ISIN?"
                  onConfirm={() => handleDelete(record.isin)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button 
                    icon={<DeleteOutlined />}
                    danger
                    size="small"
                    style={{ 
                      width: 32, 
                      height: 32, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}
                  />
                </Popconfirm>
              </Tooltip>
            )
          }
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
        <div className="loading-container">
          <Spin size="large" />
          <Text>Loading ISINs data...</Text>
        </div>
      </div>
    );
  }

  if (noContent) {
    return (
      <div className={`issuer-details-container ${isDark ? "dark" : ""}`}>
        <Result
          status="info"
          title="No ISIN data available for this issuer."
          subTitle="There are currently no ISIN records for this issuer."
        />
      </div>
    );
  }

  // Only show the 'No issuer selected' error if we are in issuer-specific mode and cin is required
  // For all-ISIN view, do not show this error
  // If you want to restrict this error to only /d/issuer/isins, you can check location.pathname or similar
  // For now, just allow the page to load if cin is not present



  return (
    <div className={`admin-page ${isDark ? "dark" : ""}`}>



      {/* Hero Section */}
      <div className="admin-header-banner">
        <div className="hero-content">
          <Title level={1} className="hero-title-news">
            {cin ? issuerName : 'ISIN List'}
          </Title>
          <Paragraph className="hero-description">
            {cin
              ? 'Browse all ISINs issued by this company with detailed instrument information and analytics'
              : 'Comprehensive database of all ISIN instruments with detailed information and analytics'
            }
          </Paragraph>
        </div>
      </div>


      {/* Data Table */}
      <Card className="admin-main-card">
        <Table
          columns={tableColumns}
          dataSource={isinsData}
          rowKey="isin"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => fetchIsinsData(page, pageSize),
          }}
          scroll={{ x: true }}
          className="admin-table"
        />
      </Card>


      {/* Edit Modal */}
      <Modal
        title={`Edit ISIN - ${editData?.isin || ""}`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="update" type="primary" loading={editLoading} onClick={handleUpdate}>
            Update
          </Button>,
        ]}
        width={800}
        destroyOnClose
      >
        {editLoading && !editData ? (
          <Spin size="large" />
        ) : (
          <Form
            form={editForm}
            layout="vertical"
            initialValues={editData}
            disabled={editLoading}
          >
            {editData && (() => {
              const fields = Object.entries(editData).filter(([key]) => key !== 'createdAt' && key !== 'updatedAt');
              const items = [];
              for (let i = 0; i < fields.length; i += 2) {
                const [key1, value1] = fields[i];
                const [key2, value2] = fields[i + 1] || [];
                items.push(
                  <div key={key1 + (key2 || '')} style={{ display: 'flex', width: '100%', gap: 16 }}>
                    <Form.Item
                      key={key1}
                      label={key1.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                      name={key1}
                      rules={key1 === "isin" ? [] : [{ required: false }]}
                      style={{ flex: 1 }}
                    >
                      {key1 === "isin" ? <Input disabled /> : <Input />}
                    </Form.Item>
                    {key2 && (
                      <Form.Item
                        key={key2}
                        label={key2.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                        name={key2}
                        rules={key2 === "isin" ? [] : [{ required: false }]}
                        style={{ flex: 1 }}
                      >
                        {key2 === "isin" ? <Input disabled /> : <Input />}
                      </Form.Item>
                    )}
                  </div>
                );
              }
              return items;
            })()}
          </Form>
        )}
      </Modal>
    </div>
  );
}

export default IssuerISINs;
